import { ActionIcon, Grid, Paper, Text, Tooltip } from '@mantine/core';
import { IconBook, IconBookOff } from '@tabler/icons-react';
import type { UseFormReturn } from 'react-hook-form';
import { Show } from '../../../../../react-commons/components/control-flow/Show';
import { Controlled_RichText } from '../../../../../react-commons/components/inputs/controlled/Controlled_RichText';
import { useNestedDialogFunction } from '../../../../../react-commons/dialog-provider/UseNestedDialogFunction';
import type { FormNames } from '../../../../../react-commons/utils/form/getFormNames';
import { OpenModal } from '../../../../../react-commons/utils/modals/openModal';
import { ResourceType } from '../../../resource/enumerations/ResourceType';
import {
    ProjectPropertyValidation,
    type ProjectResource,
} from '../../../resource/resources/artifacts/project/projectResource';
import {
    defaultProjectVersionPublishmentResource,
    useProjectVersionPublishmentActions,
} from '../../../resource/resources/artifacts/project/projectVersionPublishmentResource';
import { useProjectVersionActions } from '../../../resource/resources/artifacts/project/projectVersionResource';
import type { VersionInfo } from '../../../resource/services/versionInfo';
import { TOOLTIPS } from '../../../resource/tooltipTexts';
import { PropertyInputs } from '../../reusable-components/base/property-bases/PropertyInputs';
import { useDeleteResourceDialogFunction } from '../../reusable-components/dialogs/UseDeleteResourceDialogFunction';
import { VersionInfoBarDisplay } from '../../reusable-components/version-displays/VersionInfoBarDisplay';
import type { PropertyValidationInformation } from '../../util/PropertyValidationInformation';
import { ProjectVersionGraphDialog } from './version-graph-dialog/ProjectVersionGraphDialog';

export const ProjectContent = ({
    id,
    versionId,
    versionInfo,
    form,
    formNames,
    readOnly,
    hideName = false,
}: {
    id: string | undefined;
    versionId: string | undefined;
    versionInfo: VersionInfo | undefined;
    form: UseFormReturn<ProjectResource>;
    formNames: FormNames<ProjectResource>;
    readOnly: boolean;
    hideName?: boolean;
}) => {
    const deleteResourceDialogFunction = useDeleteResourceDialogFunction();
    const nestedDialog = useNestedDialogFunction();
    const projectVersionActions = useProjectVersionActions();
    const projectVersionPublishmentActions = useProjectVersionPublishmentActions();

    const projectVersionPublishment = projectVersionPublishmentActions.useGetSingleResource(versionId);

    const openVersion = projectVersionActions.useUnlockVersion();
    const lockVersion = projectVersionActions.useLockVersion();
    const closeVersion = projectVersionActions.useCloseVersion();

    const addPublishment = projectVersionPublishmentActions.useSetResource();
    const deletePublishment = projectVersionPublishmentActions.useDeleteResource();

    const getPropertyValidationInformation = (
        propertyIdentifier: ProjectPropertyValidation
    ): PropertyValidationInformation => ({
        resourceId: id,
        resourceType: ResourceType.PROJECT,
        propertyIdentifier,
    });

    return (
        <Grid>
            <Show when={!hideName}>
                <Grid.Col span={6}>
                    <PropertyInputs.Name
                        name={formNames.name}
                        control={form.control}
                        readOnly={readOnly}
                        propertyValidationInformation={getPropertyValidationInformation(ProjectPropertyValidation.NAME)}
                    />
                </Grid.Col>
            </Show>
            <Show when={!readOnly}>
                <Grid.Col span={2}>
                    <PropertyInputs.Boolean
                        style={{ paddingTop: 26 }}
                        name={formNames.isVisible}
                        control={form.control}
                        readOnly={readOnly}
                        propertyValidationInformation={getPropertyValidationInformation(
                            ProjectPropertyValidation.IS_VISIBLE
                        )}
                    />
                </Grid.Col>
            </Show>
            <Grid.Col span={readOnly ? 6 : 4}>
                <VersionInfoBarDisplay
                    isPublished={Boolean(projectVersionPublishment.data)}
                    titleToShow={readOnly ? projectVersionPublishment.data?.title : undefined}
                    versionInfo={versionInfo ?? null}
                    versionInfoPropertyIdentifier={ProjectPropertyValidation.VERSION_INFO}
                    versionResourceType={ResourceType.PROJECT}
                    versionId={versionId ?? ''}
                    customButtonIsIcon={true}
                    closeAction={
                        !readOnly ?
                            (versionId) => {
                                closeVersion.mutate({
                                    id: versionId,
                                });
                            }
                        :   undefined
                    }
                    openAction={
                        !readOnly ?
                            (versionId) => {
                                openVersion.mutate({
                                    id: versionId,
                                });
                            }
                        :   undefined
                    }
                    lockAction={
                        !readOnly ?
                            (versionId) => {
                                lockVersion.mutate({
                                    id: versionId,
                                });
                            }
                        :   undefined
                    }
                    onVersionGraphClick={() => {
                        nestedDialog.showNestedDialog(
                            <ProjectVersionGraphDialog
                                onClose={nestedDialog.closeNestedDialog}
                                projectId={id ?? ''}
                                noActions={readOnly}
                                activeId={versionId ?? ''}
                            />
                        );
                    }}
                    CustomButtons={
                        !readOnly ?
                            projectVersionPublishment.data ?
                                <Tooltip label={`Delete Publishment : ${projectVersionPublishment.data.title}`}>
                                    <ActionIcon
                                        size={'sm'}
                                        variant={'transparent'}
                                        onClick={() => {
                                            deleteResourceDialogFunction.deleteResource(
                                                projectVersionPublishment.data?.id ?? '',
                                                projectVersionPublishment.data?.title,
                                                ResourceType.PROJECT_VERSION_PUBLISHMENT,
                                                deletePublishment
                                            );
                                        }}
                                        p={2}
                                    >
                                        <IconBookOff color={'var(--mantine-color-base-button)'} />
                                    </ActionIcon>
                                </Tooltip>
                            :   <Tooltip label={'Publish Version'}>
                                    <ActionIcon
                                        size={'sm'}
                                        variant={'transparent'}
                                        onClick={() => {
                                            OpenModal.StringInputModal(
                                                'Publish Version',
                                                (value, multiLineValue) => {
                                                    addPublishment.mutate({
                                                        resource: {
                                                            ...defaultProjectVersionPublishmentResource,
                                                            title: value,
                                                            changelog: multiLineValue ?? '',
                                                        },
                                                        parentId: versionId,
                                                    });
                                                },
                                                undefined,
                                                'Title',
                                                true,
                                                'Changelog',
                                                undefined,
                                                true
                                            );
                                        }}
                                        p={2}
                                    >
                                        <IconBook color={'var(--mantine-color-base-button)'} />
                                    </ActionIcon>
                                </Tooltip>

                        :   undefined
                    }
                    versionGraphButtonTooltip={TOOLTIPS.PROJECT_VERSION_HISTORY}
                />
            </Grid.Col>
            <Grid.Col span={12}>
                <PropertyInputs.MultilineString
                    name={formNames.description}
                    nullable={false}
                    control={form.control}
                    readOnly={readOnly}
                    propertyValidationInformation={getPropertyValidationInformation(
                        ProjectPropertyValidation.DESCRIPTION
                    )}
                />
            </Grid.Col>
            {projectVersionPublishment.data && (
                <Grid.Col span={12}>
                    <Text fz={14}>Changelog</Text>
                    <Paper pl={10} pr={10} withBorder bg={'var(--mantine-color-read-only-input)'}>
                        <Controlled_RichText value={projectVersionPublishment.data.changelog} readonly={true} />
                    </Paper>
                </Grid.Col>
            )}
        </Grid>
    );
};
